/** @format */

import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	inject,
	Injector,
	input,
	PLATFORM_ID,
	signal
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
	openProductReviewDrawer,
	ProductReviewAnalyticsService,
	ProductReviewDrawerType,
	selectProductReviewCount
} from '@product-reviews/data-access';

import { HmfSkeletonComponent, HmfStarRatingsComponent } from '@dsg-tech/homefield-angular';
import { Store } from '@ngrx/store';
import { BreakpointsService } from '@shared/data-access/services';
import { BreakPointNew } from '@shared/data-access/enums';
import { ReviewButtonComponent } from '../review-button/review-button.component';
import { StarRatingSize } from '@dsg-tech/homefield-utils';

@Component({
	selector: 'product-reviews-rating-summary-stars',
	templateUrl: './review-rating-summary-stars.component.html',
	styleUrl: './review-rating-summary-stars.component.scss',
	imports: [CommonModule, HmfStarRatingsComponent, ReviewButtonComponent, HmfSkeletonComponent],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReviewRatingSummaryStarsComponent implements AfterViewInit {
	private readonly analyticsService = inject(ProductReviewAnalyticsService);
	private readonly store = inject(Store);
	private readonly breakpointsService = inject(BreakpointsService);
	private readonly injector = inject(Injector);
	private readonly platformId = inject(PLATFORM_ID);
	protected readonly isPlatformBrowser = isPlatformBrowser(this.platformId);

	readonly productPartNumber = input.required<string>();
	readonly ratingCount = input<number | undefined>();
	readonly ratingValue = input<number | undefined>();
	readonly isDrawerAvailable = input<boolean>();
	readonly useProductCatalogRatingData = input(true);

	readonly bvReviewCount = this.store.selectSignal(selectProductReviewCount);

	readonly ratingCountByFlag = computed(() => {
		return this.useProductCatalogRatingData() ? this.ratingCount() : this.bvReviewCount();
	});

	readonly _ratingSizeOverride = signal<StarRatingSize | null>(null);
	readonly _ratingSize = computed(() =>
		this.breakpointsService.breakpoint() <= BreakPointNew.SMALL ? 'small' : 'medium'
	);
	readonly ratingSize = computed(() => this._ratingSizeOverride() ?? this._ratingSize());
	readonly ratingValueDisplay = computed(() => {
		const rawRating = this.ratingValue();
		if (rawRating) {
			return Math.round(rawRating * 10) / 10;
		}
		return rawRating;
	});

	ngAfterViewInit(): void {
		effect(
			() => {
				if (
					this.isPlatformBrowser &&
					!this.useProductCatalogRatingData() &&
					this.isDrawerAvailable() &&
					this.bvReviewCount()
				) {
					// Keep BV from scrolling to highlight container when we open drawer (they call scroll into view on their side)
					const highlightElement = document?.getElementById(
						'product-reviews-highlight-container'
					);
					if (highlightElement) {
						// eslint-disable-next-line @typescript-eslint/no-empty-function
						highlightElement.scrollIntoView = () => {};
					}
				}
			},
			{ injector: this.injector }
		);

		/**
		 * Workaround for https://dcsgcloud.atlassian.net/browse/HMF-939
		 * hmf-star-rating does not apply styles properly, so if the size changes after hydration, it just appends the new class
		 * causing the original style to still apply. Setting it to small first, then to the correct size in the next microtask
		 * essentially "clears" the pre-hydration class and causes the correct classes to be the only classes applied.
		 **/
		this._ratingSizeOverride.set('small');
		queueMicrotask(() => {
			this._ratingSizeOverride.set(null);
		});
	}

	public handleClick() {
		this.trackReviewSummaryClicked();

		if (this.isDrawerAvailable() && this.ratingCountByFlag()) {
			this.openReviewsDrawer();
		}
	}

	openReviewsDrawer() {
		const contentListDrawer = { drawerType: ProductReviewDrawerType.REVIEWS };
		this.store.dispatch(openProductReviewDrawer(contentListDrawer));
	}

	private trackReviewSummaryClicked() {
		const payload = {
			event: 'ReviewSummaryClicked',
			value: 'Summary'
		};

		this.analyticsService.fireReviewAnalytics<{ [key: string]: string }>({
			name: 'multiUseAnalyticsEvent',
			payload
		});
	}
}
